import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'

/**
 * Ingreso con el proveedor de identidad de la agencia (§17).
 *
 * Flujo de código de autorización con PKCE, que es lo que corresponde a una aplicación de navegador:
 * no guarda ningún secreto, el MFA lo exige el proveedor y PROTECT solo valida el token que emite.
 * Nada del proveedor va compilado aquí: la configuración se pregunta al backend
 * (`/api/capabilities`), así la misma compilación sirve para una agencia con Entra ID y para otra
 * con Okta o Keycloak.
 */
export interface OidcSettings {
  authority: string
  clientId: string
  audience: string | null
}

export function createSignIn(settings: OidcSettings): UserManager {
  return new UserManager({
    authority: settings.authority,
    client_id: settings.clientId,
    redirect_uri: `${window.location.origin}${window.location.pathname}`,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    // Solo 'openid': lo que PROTECT necesita (tenant, agencia, rol) viaja en el ámbito por
    // omisión que el proveedor tiene asignado al cliente, sin que el frontend tenga que pedirlo.
    scope: 'openid',
    // El token vive en memoria de la pestaña: no sobrevive a cerrarla, que es lo que se quiere en
    // una estación de mando compartida.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
    monitorSession: false,
  })
}

/**
 * Termina el ingreso si la página se abrió como vuelta del proveedor, y deja la barra de
 * direcciones limpia (el código de autorización no tiene por qué quedar a la vista ni en el
 * historial).
 */
export async function completeSignIn(manager: UserManager): Promise<User | null> {
  const parameters = new URLSearchParams(window.location.search)
  if (!parameters.has('code') && !parameters.has('error')) return await manager.getUser()
  try {
    const user = await manager.signinRedirectCallback()
    window.history.replaceState({}, '', window.location.pathname)
    return user
  } catch {
    window.history.replaceState({}, '', window.location.pathname)
    return null
  }
}
